import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Home, Plus, Pencil, Trash2, Paperclip, Tag, FileText, Download, Loader2, X, Upload, Image as ImageIcon } from "lucide-react";
import { API } from "@/context/AuthContext";

const PROPERTIES = ["156 Rainham Road", "176 Rainham Road"];
const NO_CODE = "__none__";
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".jpg,.jpeg,.png,.pdf";
const emptyForm = { description: "", date: "", cost: "", budget_code: "", notes: "" };
const money = (n) => `${(n || 0) < 0 ? "-" : ""}£${Math.abs(n || 0).toFixed(2)}`;
const moneyColor = (n, base = "text-red-600") => ((n || 0) < 0 ? "text-green-600" : base);
const errMsg = (err, fallback) => err.response?.data?.detail || fallback;
const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const checkFiles = (files) => {
  for (const f of files) {
    if (!/\.(jpe?g|png|pdf)$/i.test(f.name)) return `${f.name}: only JPG, PNG or PDF files are allowed`;
    if (f.size > MAX_BYTES) return `${f.name} is larger than 10 MB`;
  }
  return null;
};

const uploadReceipts = (expenseId, files, onProgress) => {
  const fd = new FormData();
  files.forEach((f) => fd.append("files", f));
  return axios.post(`${API}/budget-expenses/${expenseId}/receipts`, fd, {
    onUploadProgress: (e) => e.total && onProgress?.(Math.round((e.loaded / e.total) * 100)),
  });
};

export const BudgetExpenseSection = ({ canWrite }) => {
  const [codes, setCodes] = useState([]);
  const [version, setVersion] = useState(0);
  const loadCodes = () => axios.get(`${API}/budget-codes`).then((r) => setCodes(r.data)).catch(() => toast.error("Failed to load budget codes"));
  useEffect(() => { loadCodes(); }, []);
  const onCodesChanged = () => { loadCodes(); setVersion((v) => v + 1); };

  return (
    <div className="space-y-6" data-testid="budget-expense-section">
      <BudgetCodesCard codes={codes} canWrite={canWrite} onChanged={onCodesChanged} />
      {PROPERTIES.map((p) => <BudgetPropertyTable key={p} property={p} codes={codes} canWrite={canWrite} version={version} />)}
    </div>
  );
};

const BudgetCodesCard = ({ codes, canWrite, onChanged }) => {
  const [form, setForm] = useState({ code: "", description: "" });
  const [editingId, setEditingId] = useState(null);
  const reset = () => { setForm({ code: "", description: "" }); setEditingId(null); };

  const save = async () => {
    if (!form.code.trim()) { toast.error("Enter a budget code"); return; }
    try {
      if (editingId) await axios.put(`${API}/budget-codes/${editingId}`, form);
      else await axios.post(`${API}/budget-codes`, form);
      toast.success(editingId ? "Budget code updated" : "Budget code added");
      reset();
      onChanged();
    } catch (err) { toast.error(errMsg(err, "Failed to save budget code")); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete budget code ${c.code}?`)) return;
    try {
      await axios.delete(`${API}/budget-codes/${c.id}`);
      toast.success("Budget code deleted");
      if (editingId === c.id) reset();
      onChanged();
    } catch (err) { toast.error(errMsg(err, "Failed to delete budget code")); }
  };

  return (
    <Card className="border-0 shadow-lg" data-testid="budget-codes-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2"><Tag className="w-5 h-5 text-red-500" />Budget Codes</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {canWrite && (
          <div className="grid md:grid-cols-[200px_1fr_auto_auto] gap-2 items-end">
            <div><Label className="text-sm text-gray-600 mb-1 block">Code *</Label>
              <Input placeholder="e.g. MNT-01" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="budget-code-input" /></div>
            <div><Label className="text-sm text-gray-600 mb-1 block">What it covers</Label>
              <Input placeholder="e.g. Maintenance & repairs" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="budget-code-description-input" /></div>
            <Button onClick={save} className="rounded-full bg-red-500 hover:bg-red-600" data-testid="budget-code-save-btn">
              {editingId ? "Update Code" : <><Plus className="w-4 h-4 mr-1" /> Add Code</>}
            </Button>
            {editingId && <Button variant="outline" className="rounded-full" onClick={reset} data-testid="budget-code-cancel-btn">Cancel</Button>}
          </div>
        )}
        {codes.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="budget-codes-empty">No budget codes yet. Add one to use it on expenses.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {codes.map((c) => (
              <div key={c.id} className="flex items-center gap-2 pl-3 pr-1 py-1 rounded-full border bg-gray-50" data-testid={`budget-code-chip-${c.id}`}>
                <span className="font-semibold text-sm">{c.code}</span>
                {c.description && <span className="text-xs text-gray-500">{c.description}</span>}
                {canWrite && (
                  <>
                    <Button size="icon" variant="ghost" className="h-7 w-7 rounded-full" onClick={() => { setEditingId(c.id); setForm({ code: c.code, description: c.description || "" }); }} data-testid={`budget-code-edit-${c.id}`}><Pencil className="w-3 h-3" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 rounded-full" onClick={() => remove(c)} data-testid={`budget-code-delete-${c.id}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BudgetPropertyTable = ({ property, codes, canWrite, version }) => {
  const slug = property.split(" ")[0];
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [receiptsFor, setReceiptsFor] = useState(null);

  const load = () => axios.get(`${API}/budget-expenses`, { params: { property } }).then((r) => setRows(r.data)).catch(() => toast.error(`Failed to load ${property} expenses`));
  useEffect(() => { load(); }, [version]);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setPendingFiles([]); setOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({ description: r.description, date: r.date, cost: String(r.cost), budget_code: r.budget_code || "", notes: r.notes || "" });
    setPendingFiles([]);
    setOpen(true);
  };

  const save = async () => {
    if (!form.description.trim() || !form.date || form.cost === "" || isNaN(parseFloat(form.cost))) {
      toast.error("Please fill in description, date and cost");
      return;
    }
    const fileError = checkFiles(pendingFiles);
    if (fileError) { toast.error(fileError); return; }
    setSaving(true);
    try {
      const payload = { ...form, property, cost: parseFloat(form.cost) };
      const res = editingId ? await axios.put(`${API}/budget-expenses/${editingId}`, payload) : await axios.post(`${API}/budget-expenses`, payload);
      toast.success(editingId ? "Expense updated" : "Expense added");
      if (pendingFiles.length) {
        await uploadReceipts(res.data.id, pendingFiles).catch((e) => toast.error(errMsg(e, "Expense saved, but receipts failed to upload")));
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save expense"));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this budget expense and its receipts?")) return;
    try { await axios.delete(`${API}/budget-expenses/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };

  const total = rows.reduce((s, r) => s + (r.cost || 0), 0);
  const receiptsExpense = rows.find((r) => r.id === receiptsFor);

  return (
    <Card className="border-0 shadow-lg" data-testid={`budget-table-card-${slug}`}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Home className="w-5 h-5 text-red-500" />{property}</CardTitle>
        {canWrite && (
          <Button onClick={openAdd} className="rounded-full bg-red-500 hover:bg-red-600" data-testid={`budget-add-btn-${slug}`}>
            <Plus className="w-4 h-4 mr-2" /> Add Expense
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <BudgetExpenseRows rows={rows} total={total} slug={slug} canWrite={canWrite} onEdit={openEdit} onDelete={remove} onReceipts={setReceiptsFor} />
        <CodeTotals rows={rows} codes={codes} slug={slug} />
      </CardContent>
      <BudgetExpenseDialog open={open} onOpenChange={setOpen} form={form} setForm={setForm} onSave={save} editing={!!editingId}
        slug={slug} codes={codes} pendingFiles={pendingFiles} setPendingFiles={setPendingFiles} saving={saving} />
      {receiptsExpense && <ReceiptsDialog expense={receiptsExpense} canWrite={canWrite} onClose={() => setReceiptsFor(null)} onChanged={load} />}
    </Card>
  );
};

const BudgetExpenseDialog = ({ open, onOpenChange, form, setForm, onSave, editing, slug, codes, pendingFiles, setPendingFiles, saving }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid={`budget-dialog-${slug}`}>
      <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Budget Expense</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label className="text-sm text-gray-600 mb-1 block">Expenses Description *</Label>
          <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid={`budget-input-description-${slug}`} /></div>
        <div><Label className="text-sm text-gray-600 mb-1 block">Date *</Label>
          <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid={`budget-input-date-${slug}`} /></div>
        <div><Label className="text-sm text-gray-600 mb-1 block">Cost Value (£) *</Label>
          <Input type="number" step="0.01" placeholder="0.00 (use minus for refunds/credits)" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} data-testid={`budget-input-cost-${slug}`} /></div>
        <div><Label className="text-sm text-gray-600 mb-1 block">Budget Code</Label>
          <Select value={form.budget_code || NO_CODE} onValueChange={(v) => setForm({ ...form, budget_code: v === NO_CODE ? "" : v })}>
            <SelectTrigger data-testid={`budget-input-code-${slug}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_CODE}>No code</SelectItem>
              {codes.map((c) => <SelectItem key={c.id} value={c.code} data-testid={`budget-code-option-${slug}-${c.code}`}>{c.code}{c.description ? ` – ${c.description}` : ""}</SelectItem>)}
            </SelectContent>
          </Select>
          {codes.length === 0 && <p className="text-xs text-gray-500 mt-1">Add codes in the Budget Codes box above.</p>}
        </div>
        <div><Label className="text-sm text-gray-600 mb-1 block">Notes/Comments</Label>
          <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid={`budget-input-notes-${slug}`} /></div>
        <div><Label className="text-sm text-gray-600 mb-1 block">Attach Receipts (JPG, PNG, PDF · max 10 MB each)</Label>
          <Input type="file" multiple accept={ACCEPT} onChange={(e) => setPendingFiles(Array.from(e.target.files || []))} data-testid={`budget-input-receipts-${slug}`} />
          {pendingFiles.length > 0 && <p className="text-xs text-gray-500 mt-1">{pendingFiles.length} file(s) will be attached when you save</p>}
        </div>
        <Button onClick={onSave} disabled={saving} className="w-full rounded-full bg-red-500 hover:bg-red-600" data-testid={`budget-save-btn-${slug}`}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editing ? "Update Expense" : "Save Expense"}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
);

const BudgetExpenseRows = ({ rows, total, slug, canWrite, onEdit, onDelete, onReceipts }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={`budget-table-${slug}`}>
      <thead>
        <tr className="border-b border-gray-200">
          <th className="text-left py-3 px-2 font-semibold text-gray-600">Expenses Description</th>
          <th className="text-left py-3 px-2 font-semibold text-gray-600">Date</th>
          <th className="text-right py-3 px-2 font-semibold text-gray-600">Cost Value (£)</th>
          <th className="text-left py-3 px-2 font-semibold text-gray-600">Budget Code</th>
          <th className="text-left py-3 px-2 font-semibold text-gray-600">Notes/Comments</th>
          <th className="text-left py-3 px-2 font-semibold text-gray-600">Receipts</th>
          {canWrite && <th className="text-right py-3 px-2 font-semibold text-gray-600">Actions</th>}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={canWrite ? 7 : 6} className="text-center text-gray-500 py-8" data-testid={`budget-empty-${slug}`}>No expenses recorded yet</td></tr>
        ) : rows.map((r) => (
          <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`budget-row-${r.id}`}>
            <td className="py-3 px-2 font-medium">{r.description}</td>
            <td className="py-3 px-2 text-gray-600">{r.date ? new Date(r.date + "T12:00:00").toLocaleDateString("en-GB") : "-"}</td>
            <td className={`py-3 px-2 text-right whitespace-nowrap ${moneyColor(r.cost, "")}`} data-testid={`budget-cost-${r.id}`}>{money(r.cost)}</td>
            <td className="py-3 px-2 text-gray-600">{r.budget_code || "-"}</td>
            <td className="py-3 px-2 text-gray-500 max-w-[260px] whitespace-pre-wrap">{r.notes || "-"}</td>
            <td className="py-3 px-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => onReceipts(r.id)} data-testid={`budget-receipts-btn-${r.id}`}>
                <Paperclip className="w-3 h-3 mr-1" />{r.receipts?.length || 0}
              </Button>
            </td>
            {canWrite && (
              <td className="py-3 px-2 text-right">
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="outline" onClick={() => onEdit(r)} data-testid={`budget-edit-${r.id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="destructive" onClick={() => onDelete(r.id)} data-testid={`budget-delete-${r.id}`}><Trash2 className="w-3 h-3" /></Button>
                </div>
              </td>
            )}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t-2 border-gray-300 font-bold">
          <td className="py-3 px-2" colSpan={2}>Total</td>
          <td className={`py-3 px-2 text-right whitespace-nowrap ${moneyColor(total)}`} data-testid={`budget-total-${slug}`}>{money(total)}</td>
          <td colSpan={canWrite ? 4 : 3}></td>
        </tr>
      </tfoot>
    </table>
  </div>
);

const CodeTotals = ({ rows, codes, slug }) => {
  if (!rows.length) return null;
  const sums = {};
  rows.forEach((r) => { const k = r.budget_code || ""; sums[k] = (sums[k] || 0) + (r.cost || 0); });
  const desc = Object.fromEntries(codes.map((c) => [c.code, c.description]));
  const keys = Object.keys(sums).sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));
  return (
    <div className="rounded-xl bg-gray-50 p-4" data-testid={`budget-code-totals-${slug}`}>
      <p className="text-sm font-semibold text-gray-700 mb-2">Totals by Budget Code</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {keys.map((k) => (
          <div key={k || NO_CODE} className="flex justify-between gap-3 bg-white rounded-lg px-3 py-2 text-sm border" data-testid={`budget-code-total-${slug}-${k || "none"}`}>
            <span className="truncate"><span className="font-semibold">{k || "No code"}</span>{desc[k] && <span className="text-gray-500"> · {desc[k]}</span>}</span>
            <span className={`font-bold whitespace-nowrap ${moneyColor(sums[k])}`}>{money(sums[k])}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReceiptsDialog = ({ expense, canWrite, onClose, onChanged }) => {
  const [progress, setProgress] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingId, setLoadingId] = useState(null);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);

  const view = async (rc) => {
    setLoadingId(rc.id);
    try {
      const res = await axios.get(`${API}/budget-receipts/${rc.id}/download`, { responseType: "blob" });
      setPreview({ id: rc.id, url: URL.createObjectURL(res.data), type: rc.content_type, name: rc.filename });
    } catch { toast.error("Could not open receipt"); }
    setLoadingId(null);
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const fileError = checkFiles(files);
    if (fileError) { toast.error(fileError); return; }
    setProgress(0);
    try { await uploadReceipts(expense.id, files, setProgress); toast.success(`${files.length} receipt(s) attached`); onChanged(); }
    catch (err) { toast.error(errMsg(err, "Upload failed")); }
    setProgress(null);
  };

  const remove = async (rc) => {
    if (!window.confirm(`Remove ${rc.filename}?`)) return;
    try {
      await axios.delete(`${API}/budget-receipts/${rc.id}`);
      if (preview?.id === rc.id) setPreview(null);
      toast.success("Receipt removed");
      onChanged();
    } catch { toast.error("Failed to remove receipt"); }
  };

  const receipts = expense.receipts || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="budget-receipts-dialog">
        <DialogHeader><DialogTitle>Receipts · {expense.description}</DialogTitle></DialogHeader>
        {canWrite && (
          <label className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-xl p-4 text-sm text-gray-600 hover:bg-red-50 hover:border-red-300 transition-colors ${progress === null ? "cursor-pointer" : "opacity-70"}`} data-testid="budget-receipts-upload-label">
            {progress === null ? <Upload className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}
            {progress === null ? "Attach receipts (JPG, PNG, PDF · max 10 MB each)" : `Uploading… ${progress}%`}
            <input type="file" multiple accept={ACCEPT} className="hidden" onChange={upload} disabled={progress !== null} data-testid="budget-receipts-upload-input" />
          </label>
        )}
        {receipts.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4" data-testid="budget-receipts-empty">No receipts attached yet</p>
        ) : (
          <div className="space-y-2">
            {receipts.map((rc) => (
              <div key={rc.id} className="flex items-center justify-between gap-2 border rounded-lg px-3 py-2" data-testid={`budget-receipt-${rc.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  {rc.content_type === "application/pdf" ? <FileText className="w-4 h-4 text-red-500 shrink-0" /> : <ImageIcon className="w-4 h-4 text-blue-500 shrink-0" />}
                  <span className="text-sm truncate">{rc.filename}</span>
                  <span className="text-xs text-gray-400 shrink-0">{fmtSize(rc.size)}</span>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => view(rc)} disabled={loadingId === rc.id} data-testid={`budget-receipt-view-${rc.id}`}>
                    {loadingId === rc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "View"}
                  </Button>
                  {canWrite && <Button size="sm" variant="ghost" onClick={() => remove(rc)} data-testid={`budget-receipt-delete-${rc.id}`}><Trash2 className="w-3 h-3 text-red-500" /></Button>}
                </div>
              </div>
            ))}
          </div>
        )}
        {preview && <ReceiptPreview preview={preview} onClose={() => setPreview(null)} />}
      </DialogContent>
    </Dialog>
  );
};

const ReceiptPreview = ({ preview, onClose }) => (
  <div className="border rounded-xl p-3 space-y-2" data-testid="budget-receipt-preview">
    <div className="flex justify-between items-center gap-2">
      <p className="text-sm font-semibold truncate">{preview.name}</p>
      <div className="flex gap-1 shrink-0">
        <Button size="sm" variant="outline" asChild>
          <a href={preview.url} download={preview.name} data-testid="budget-receipt-download-btn"><Download className="w-3 h-3 mr-1" /> Download</a>
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose} data-testid="budget-receipt-preview-close"><X className="w-4 h-4" /></Button>
      </div>
    </div>
    {preview.type === "application/pdf"
      ? <iframe src={preview.url} title={preview.name} className="w-full h-[60vh] rounded-lg border" />
      : <img src={preview.url} alt={preview.name} className="max-h-[60vh] mx-auto rounded-lg" />}
  </div>
);
